import {
    fetchPushPublicKeyApi,
    registerPushSubscriptionApi,
    removePushSubscriptionApi,
} from '../services/notificationApi';

/**
 * Turning browser push notifications on and off for THIS device.
 *
 * ## What this file has to get right
 *
 * Three separate things must all be true before a notification can reach a phone that
 * has the app closed, and each fails differently:
 *
 *   1. the browser supports push at all,
 *   2. the user has granted permission,
 *   3. the server has a VAPID key pair configured.
 *
 * They are reported separately rather than as one boolean because the fix for each is
 * different: install the app, tap allow, or configure the server. A single "push is
 * off" leaves the person with no idea which.
 *
 * ## iOS
 *
 * ⚠️ Safari exposes PushManager ONLY when the PWA has been installed to the home
 * screen. An iPhone user browsing in Safari gets `unsupported`, and the honest answer
 * is "add this to your home screen first" — not a button that silently does nothing.
 *
 * ## Permission must be asked from a tap
 *
 * `Notification.requestPermission()` called on page load is ignored by every modern
 * browser, and worse, a dismissed prompt can be remembered as a denial. So nothing
 * here runs on mount: `enablePush` is only ever called from a button.
 */

/** Push is off, and why — so the UI can say what to do about it. */
export const PushState = {
    UNSUPPORTED: 'unsupported',
    NOT_CONFIGURED: 'not-configured',
    /** The browser can do push, but no service worker is running to receive it. */
    NO_WORKER: 'no-worker',
    DENIED: 'denied',
    PROMPT: 'prompt',
    ENABLED: 'enabled',
};

/**
 * How long to wait for a service worker to become active before giving up.
 *
 * ⚠️ THE REASON THIS EXISTS. `navigator.serviceWorker.ready` never rejects and never
 * times out — with no worker registered it simply never settles. registerServiceWorker
 * returns early unless import.meta.env.PROD, so in `npm run dev` there is no worker at
 * all, and awaiting `ready` hangs forever: the push state stays null, the prompt card
 * never renders, and the screen shows nothing with no error anywhere. That is exactly
 * what a blank Notifications page in dev turned out to be.
 */
const WORKER_READY_TIMEOUT_MS = 3000;

/** The active registration, or null — never a promise that hangs. */
const activeRegistration = async () => {
    let settle;
    const timeout = new Promise((resolve) => { settle = setTimeout(() => resolve(null), WORKER_READY_TIMEOUT_MS); });
    try {
        return await Promise.race([navigator.serviceWorker.ready, timeout]);
    } finally {
        clearTimeout(settle);
    }
};

/** True when this browser can do web push at all. False in iOS Safari until installed. */
export const isPushSupported = () =>
    typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;

/**
 * The VAPID key arrives base64url and `PushManager.subscribe` wants raw bytes.
 * ⚠️ Not interchangeable: passing the string through gives an
 * "applicationServerKey is not properly formatted" error that names nothing useful.
 */
export const urlBase64ToUint8Array = (base64UrlString) => {
    const padding = '='.repeat((4 - (base64UrlString.length % 4)) % 4);
    const base64 = (base64UrlString + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
    return bytes;
};

/** The browser's own key material, base64url, as the server stores it. */
const encodeKey = (subscription, name) => {
    const key = subscription.getKey(name);
    if (!key) return null;
    return window.btoa(String.fromCharCode(...new Uint8Array(key)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * What state push is in for this device, without prompting for anything.
 * Safe to call on mount — it asks no permission and shows no dialog.
 */
export const getPushState = async () => {
    if (!isPushSupported()) return PushState.UNSUPPORTED;
    if (Notification.permission === 'denied') return PushState.DENIED;

    let configured;
    try {
        configured = await fetchPushPublicKeyApi();
    } catch {
        // The server is unreachable; do not claim push is broken on the device.
        return PushState.PROMPT;
    }
    if (!configured?.enabled || !configured.publicKey) return PushState.NOT_CONFIGURED;

    const registration = await activeRegistration();
    if (!registration) return PushState.NO_WORKER;

    const existing = await registration.pushManager.getSubscription();
    return existing ? PushState.ENABLED : PushState.PROMPT;
};

/**
 * Asks permission, subscribes, and registers the subscription with the server.
 *
 * ⚠️ MUST be called from a user gesture. See the file comment.
 *
 * @returns the resulting {@link PushState}
 */
export const enablePush = async () => {
    if (!isPushSupported()) return PushState.UNSUPPORTED;

    const { enabled, publicKey } = await fetchPushPublicKeyApi();
    if (!enabled || !publicKey) return PushState.NOT_CONFIGURED;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        return permission === 'denied' ? PushState.DENIED : PushState.PROMPT;
    }

    const registration = await activeRegistration();
    if (!registration) return PushState.NO_WORKER;

    // Reuse an existing subscription rather than creating a second one: the browser
    // keeps one per registration, and subscribing again with a different key throws.
    const subscription = await registration.pushManager.getSubscription()
        ?? await registration.pushManager.subscribe({
            // Required by every browser now — a subscription that cannot show a
            // notification is not allowed to exist.
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

    await registerPushSubscriptionApi({
        endpoint: subscription.endpoint,
        p256dh: encodeKey(subscription, 'p256dh'),
        auth: encodeKey(subscription, 'auth'),
        // So a mechanic can tell their phone from the workshop tablet.
        userAgent: navigator.userAgent?.slice(0, 255) ?? null,
    });

    return PushState.ENABLED;
};

/**
 * Stops push on this device: unsubscribes in the browser AND removes the row.
 *
 * ⚠️ Both, in that order. Unsubscribing alone leaves the server pushing to a dead
 * endpoint until the push service reports it gone; deleting the row alone leaves the
 * browser subscribed and re-registering on the next visit.
 */
export const disablePush = async () => {
    if (!isPushSupported()) return PushState.UNSUPPORTED;

    const registration = await activeRegistration();
    if (!registration) return PushState.NO_WORKER;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return PushState.PROMPT;

    const { endpoint } = subscription;
    await subscription.unsubscribe();
    try {
        await removePushSubscriptionApi(endpoint);
    } catch {
        // The browser has already stopped; a stale row is cleaned up by the sender the
        // first time the push service answers 410 for it.
    }
    return PushState.PROMPT;
};
